import React, { useState, useEffect, useRef } from 'react';
import { useCanvas } from '../../hooks/useCanvas';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '../../utils/constants';

/**
 * PERFORMANCE OPTIMIZATION STRATEGIES
 * 
 * If FPS drops below 30 during stress testing, consider these optimizations:
 * 
 * 1. VIEWPORT CULLING:
 *    - Only render shapes within visible viewport
 *    - Use Konva's viewport calculations to filter shapes
 *    - Implement spatial indexing (quadtree) for efficient culling
 * 
 * 2. VIRTUALIZATION:
 *    - Render only shapes in current view + small buffer
 *    - Use react-window or react-virtualized for large datasets
 *    - Implement level-of-detail (LOD) rendering
 * 
 * 3. KONVA OPTIMIZATIONS:
 *    - Enable perfectDrawEnabled={false} on shapes
 *    - Use listening={false} on non-interactive shapes  
 *    - Implement shape pooling for frequently created/destroyed objects
 *    - Use caching for complex shapes: shape.cache()
 * 
 * 4. RENDERING OPTIMIZATIONS:
 *    - Batch shape updates using Layer.batchDraw()
 *    - Reduce shape complexity (fewer anchor points)
 *    - Use simpler shapes during pan/zoom operations
 *    - Implement adaptive quality based on zoom level
 * 
 * 5. STATE MANAGEMENT:
 *    - Debounce Firebase updates during rapid interactions
 *    - Use local state for temporary operations, sync periodically
 *    - Implement optimistic updates for better UX
 */

function CanvasControls() {
  const { shapes, selectedId, selectedIds, clearSelection, addShape, deleteShape, deleteAllShapes, getViewportCenter } = useCanvas();
  
  // Dev mode detection
  const isDevMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  
  // FPS monitoring state
  const [fps, setFps] = useState(0);
  const [isStressTesting, setIsStressTesting] = useState(false);
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());

  const handleAddRectangle = () => {
    const center = getViewportCenter();
    console.log('➕ Adding rectangle');
    addShape({
      type: 'rectangle',
      x: center.x - 50,
      y: center.y - 40,
      width: 100,
      height: 80
    });
  };

  const handleAddCircle = () => {
    const center = getViewportCenter();
    console.log('➕ Adding circle');
    addShape({
      type: 'circle',
      x: center.x,
      y: center.y,
      radius: 50
    });
  };

  const handleAddText = () => {
    const center = getViewportCenter();
    console.log('➕ Adding text');
    addShape({
      type: 'text',
      x: center.x - 25,
      y: center.y - 10,
      text: 'Text',
      fontSize: 18
    });
  };

  const handleAddLine = () => {
    const center = getViewportCenter();
    console.log('➕ Adding line');
    addShape({
      type: 'line',
      x: center.x - 50,
      y: center.y,
      points: [0, 0, 100, 0],
      stroke: '#000000',
      strokeWidth: 2
    });
  };

  const handleDeleteSelected = () => {
    if (selectedId) {
      const success = deleteShape(selectedId);
      if (!success) {
        console.warn('Could not delete selected shape - it may be locked by another user');
      }
    }
  };

  const handleClearSelection = () => {
    clearSelection();
  };

  const handleDeleteAll = () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ALL ${shapes.length} shapes? This cannot be undone.`
    );
    
    if (confirmed) {
      deleteAllShapes();
    }
  };

  // FPS Monitoring
  useEffect(() => {
    let animationId;
    
    const measureFPS = () => {
      frameCountRef.current++;
      const currentTime = performance.now();
      
      if (currentTime - lastTimeRef.current >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / (currentTime - lastTimeRef.current)));
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }
      
      animationId = requestAnimationFrame(measureFPS);
    };
    
    animationId = requestAnimationFrame(measureFPS);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  // Stress test function - creates many shapes for performance testing
  const createManyShapes = async () => {
    if (isStressTesting) return;
    
    setIsStressTesting(true);
    console.log('🚀 Starting stress test - creating 500 shapes...');
    
    const startTime = performance.now();
    const colors = ['#FF5733', '#33C1FF', '#8E44AD', '#F39C12', '#27AE60', '#E74C3C', '#3498DB', '#9B59B6'];
    
    try {
      for (let i = 0; i < 500; i++) {
        // Random position within canvas bounds
        const x = Math.random() * (CANVAS_WIDTH - 100);
        const y = Math.random() * (CANVAS_HEIGHT - 100);
        
        // Random size and color
        const width = 50 + Math.random() * 100;
        const height = 50 + Math.random() * 100;
        const fill = colors[Math.floor(Math.random() * colors.length)];
        
        await addShape({
          x,
          y,
          width,
          height,
          fill
        });
        
        // Small delay to prevent overwhelming Firebase
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Log progress every 100 shapes
        if (i % 100 === 0) {
          console.log(`📊 Created ${i + 1} shapes...`);
        }
      }
      
      const endTime = performance.now();
      console.log(`✅ Stress test completed in ${((endTime - startTime) / 1000).toFixed(2)} seconds`);
      console.log(`📈 Total shapes: ${shapes.length + 500}`);
      console.log(`🎯 Performance tips:`);
      console.log(`   - Monitor FPS while panning/zooming`);
      console.log(`   - Consider virtualization if FPS < 30`);
      console.log(`   - Use Konva performance optimizations`);
      console.log(`   - Limit shapes to visible viewport`);
      
    } catch (error) {
      console.error('❌ Stress test failed:', error);
    } finally {
      setIsStressTesting(false);
    }
  };

  // Clear all shapes function for cleanup after stress test
  const clearAllShapes = () => {
    if (window.confirm(`Are you sure you want to delete all ${shapes.length} shapes? This cannot be undone.`)) {
      console.log('🧹 Clearing all shapes...');
      deleteAllShapes();
    }
  };

  return (
    <>
      {/* Canvas Info Overlay - Bottom Left */}
      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl px-5 py-4 text-sm text-gray-700 border border-white/50">
        <div className="flex items-center space-x-4">
          <div>
            <span className="font-medium">Shapes:</span> {shapes.length}
          </div>
          <div>
            <span className="font-medium">Selected:</span> {selectedId || 'None'}
          </div>
          <div>
            <span className="font-medium">FPS:</span> 
            <span className={`ml-1 ${fps < 30 ? 'text-red-500' : fps < 50 ? 'text-yellow-500' : 'text-green-500'}`}>
              {fps}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Drag to pan • Scroll to zoom • Click shapes to select
          {shapes.length > 1000 && (
            <span className="text-yellow-600 block mt-1">
              ⚠️ High shape count may affect performance
            </span>
          )}
        </div>
      </div>

      {/* Toolbar - Top Right */}
      <div className="absolute top-20 right-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl border border-white/50">
        <div className="p-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 px-2">Tools</h3>
          
          {/* Shape Creation */}
          <div className="space-y-2">
            <button
              onClick={handleAddRectangle}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16v12H4z" />
              </svg>
              Rectangle
            </button>

            <button
              onClick={handleAddCircle}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-all duration-200 group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" strokeWidth={2} />
              </svg>
              Circle
            </button>

            <button
              onClick={handleAddText}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 rounded-lg transition-all duration-200 group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Text
            </button>

            <button
              onClick={handleAddLine}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-lg transition-all duration-200 group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Line
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-3"></div>

          {/* Selection Actions */}
          <div className="space-y-2">
            <button
              onClick={handleDeleteSelected}
              disabled={!selectedId}
              className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-all duration-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected
            </button>

            <button
              onClick={handleDeleteAll}
              disabled={shapes.length === 0}
              className="w-full flex items-center px-3 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-800 rounded-lg transition-all duration-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete All
            </button>

            <button
              onClick={handleClearSelection}
              disabled={!selectedId}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all duration-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Selection
            </button>
          </div>

          {/* Dev Mode Stress Testing - Hidden in production */}
          {isDevMode && (
            <>
              <div className="border-t border-gray-200 my-3"></div>
              <div className="space-y-2">
                <div className="px-2 py-1">
                  <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    DEV MODE
                  </span>
                </div>
                
                <button
                  onClick={createManyShapes}
                  disabled={isStressTesting}
                  className="w-full flex items-center px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 hover:text-orange-700 rounded-lg transition-all duration-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed group"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {isStressTesting ? 'Creating Shapes...' : 'Stress Test (500 shapes)'}
                </button>

                {shapes.length > 50 && (
                  <button
                    onClick={clearAllShapes}
                    className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-all duration-200 group"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Clear All Shapes
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Keyboard Shortcuts Help - Bottom Right */}
      <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl px-5 py-4 border border-white/50">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Reference</h4>
        <div className="space-y-1 text-xs text-gray-600">
          <div className="flex justify-between items-center space-x-4">
            <span>Delete:</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Del</kbd>
          </div>
          <div className="flex justify-between items-center space-x-4">
            <span>Delete All:</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">⇧ Del</kbd>
          </div>
          <div className="flex justify-between items-center space-x-4">
            <span>Deselect:</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Esc</kbd>
          </div>
          <div className="flex justify-between items-center space-x-4">
            <span>Rectangle:</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">R</kbd>
          </div>
          <div className="flex justify-between items-center space-x-4">
            <span>Help:</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">?</kbd>
          </div>
        </div>
      </div>

      {/* Selection Indicator */}
      {selectedId && (
        <div className="absolute top-20 left-4 bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-xl px-5 py-3 shadow-lg animate-slide-in">
          <div className="flex items-center text-sm">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3 animate-pulse"></div>
            <span className="text-blue-800 font-semibold">
              Shape Selected: {selectedId.slice(0, 8)}...
            </span>
          </div>
        </div>
      )}

      {/* Text Editing Helper - Shows when a single text shape is selected */}
      {selectedIds.length === 1 && shapes.find(s => selectedIds.includes(s.id))?.type === 'text' && (
        <div className="absolute top-36 left-4 bg-green-50/90 backdrop-blur-sm border border-green-300 rounded-xl px-5 py-3 shadow-lg animate-slide-in">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-semibold text-green-800 mb-1">💡 Text Editing Active</h4>
              <div className="text-xs text-green-700 space-y-0.5">
                <div><kbd className="px-1.5 py-0.5 bg-white rounded text-xs font-mono border border-green-300">Type</kbd> to edit text</div>
                <div><kbd className="px-1.5 py-0.5 bg-white rounded text-xs font-mono border border-green-300">Backspace</kbd> to delete character</div>
                <div><kbd className="px-1.5 py-0.5 bg-white rounded text-xs font-mono border border-green-300">Delete</kbd> to clear all text</div>
                <div><kbd className="px-1.5 py-0.5 bg-white rounded text-xs font-mono border border-green-300">Enter</kbd> for line break</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CanvasControls;
